import { Box, Center, HStack, VStack } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';

import Button from 'components/Button';
import Icon from 'components/Icon';
import PageLoader from 'components/PageLoader';
import { SEO } from 'components/SEO';
import Text from 'components/Text';

import CVPreview from './components/CVPreview';
import { useGetPublicCVQuery } from './hooks/useGetPublicCVQuery';
import { createDefaultSettings } from './utils/cvDefaults';

const CVPublicView = () => {
  const { cvId } = useParams<{ cvId: string }>();
  const { cv, isFetching } = useGetPublicCVQuery(cvId);

  if (isFetching) {
    return <PageLoader />;
  }

  if (!cv) {
    return (
      <Center height="100vh" backgroundColor="gray.900">
        <VStack spacing={4}>
          <Text fontSize="xLarge" fontWeight="bold">
            CV not found
          </Text>
          <Text fontSize="small" opacity="secondary">
            This CV may have been deleted or is no longer shared.
          </Text>
          <Button scheme="gradient" href={process.env.REACT_APP_SITE_URL}>
            Try BigDevSoon
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minHeight="100vh">
      <SEO
        title={`${cv.title} | BigDevSoon CV Builder`}
        description={`View ${cv.title} — created with BigDevSoon's AI-powered CV Builder.`}
        url={`${process.env.REACT_APP_SITE_URL}cv/${cvId}`}
      />

      {/* Header — sticky at top */}
      <HStack
        px={6}
        height="56px"
        minHeight="56px"
        backgroundColor="gray.900"
        borderBottom="1px solid"
        borderColor="whiteAlpha.100"
        justifyContent="space-between"
        alignItems="center"
        position="sticky"
        top={0}
        zIndex={10}
      >
        {/* Logo + App name */}
        <Box as="a" href={process.env.REACT_APP_SITE_URL} flexShrink={0} cursor="pointer">
          <Icon type="Logo" size={120} />
        </Box>

        {/* CV title — centered */}
        <Text
          fontSize="small"
          opacity="secondary"
          noOfLines={1}
          flex={1}
          textAlign="center"
          px={4}
        >
          {cv.title}
        </Text>

        {/* CTA */}
        <Button scheme="gradient" size="sm" href={process.env.REACT_APP_SITE_URL} flexShrink={0}>
          Build your CV
        </Button>
      </HStack>

      {/* CV Preview — natural page scroll, centered, full height */}
      <Box display="flex" justifyContent="center">
        <Box width="55%" maxWidth="800px" minWidth="320px">
          <CVPreview data={cv.data} settings={{ ...createDefaultSettings(), ...cv.settings }} template={cv.template} />
        </Box>
      </Box>

      {/* Watermark promo card — bottom-right, always visible */}
      <Box
        position="fixed"
        bottom="32px"
        right="32px"
        px={4}
        py={3}
        backgroundColor="gray.900"
        border="1px solid"
        borderColor="whiteAlpha.100"
        borderRadius="lg"
        opacity={0.85}
        _hover={{ opacity: 1 }}
        transition="opacity 0.2s"
        cursor="pointer"
        as="a"
        href={process.env.REACT_APP_SITE_URL}
        target="_blank"
        rel="noopener noreferrer"
        zIndex={20}
      >
        <VStack spacing={0} alignItems="flex-start">
          <Box style={{ fontSize: '11px', fontWeight: 600, color: 'white', lineHeight: '1.3' }}>
            Made with BigDevSoon
          </Box>
          <Box style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)', lineHeight: '1.3' }}>
            Create your professional CV in seconds
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default CVPublicView;
